import React, { useEffect, useMemo, useState } from 'react';
import { Paper, Typography } from '@material-ui/core';
import { PricingInfo } from './pricing-info';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { TertiaryButton } from '@clover/web-lib-core/components';
import {
  getMeteredBillingString,
  getSubscription, isWirelessManagerApp,
  SubscriptionObj,
} from '../../../component-helpers/app-details';
import { App } from '../../../models/app';
import PreInstallConnectButton from '../AppActions/PreInstallConnectButton';
import { AppDetailsModal } from '../../../pages/AppDetails/types';
import { getActionsForPricingModule } from '../../../component-helpers/app-actions';
import { AppActions } from '../AppActions/types';
import { PreChangeSubscriptionButton } from '../AppActions/pre-change-subscription';

interface ComponentProps {
  app: App;
  moduleId?: string;
  subscriptionObj?: SubscriptionObj;
  setModalVisibility: (modal: AppDetailsModal) => void;
}

export const AppPricingAndSubscriptionOptions: React.FC<ComponentProps> = (
  {
    app,
    moduleId,
    subscriptionObj,
    setModalVisibility,
  }): Nullable<JSX.Element> => {
  const { t } = useTranslation();
  const [expandOptions, setExpandOptions] = useState<boolean>(false);
  const subscription = useMemo((): SubscriptionObj => {
    return subscriptionObj || getSubscription(app);
  }, [app, subscriptionObj]);
  const {
    availableSubscriptionsArr,
    activeMeteredBilling,
    currentlySelectedSubscription,
    bestSubscriptionOption,
  } = subscription;
  const prioritySubscription = currentlySelectedSubscription || bestSubscriptionOption;
  const meteredBillingString = getMeteredBillingString(activeMeteredBilling);
  const actionButton = getActionsForPricingModule(app, availableSubscriptionsArr);

  useEffect(() => {
    setExpandOptions(false);
  }, [app]);

  const handleSeeMoreOrLess = (): void => {
    setExpandOptions(!expandOptions);
  };

  const openPricingModal = (): void => {
    setModalVisibility(AppDetailsModal.Pricing);
  };

  const getActionButtonComponent = (button: AppActions): Maybe<JSX.Element> => {
    let actionButtonToReturn;
    switch (button) {
      case AppActions.PreInstall:
        actionButtonToReturn = <PreInstallConnectButton app={app} actionHandler={openPricingModal} />;
        break;
      case AppActions.YelpConnect:
        actionButtonToReturn =
          <PreInstallConnectButton app={app} actionHandler={() => setModalVisibility(AppDetailsModal.YelpConnect)} />;
        break;
      case AppActions.PreChangePricing:
        actionButtonToReturn = <PreChangeSubscriptionButton app={app} onClickHandler={openPricingModal} />;
    }
    return actionButtonToReturn;
  };

  if (!availableSubscriptionsArr.length || !prioritySubscription) return null;

  return (
    <Paper square id={moduleId || 'pricing-options'}>
      <h3>{t('app_details.pricing_subscription')}</h3>
      {isWirelessManagerApp(app) && app.installed && <Typography variant='body1' color='textSecondary' gutterBottom>
        {t('app_details.wireless_manager_app_message')}
      </Typography>}
      {
        expandOptions ?
          availableSubscriptionsArr.map(availableSubscription => <PricingInfo
            subscriptionToShow={availableSubscription}
            currentSubscription={currentlySelectedSubscription}
            meteredBillingString={meteredBillingString}
          />) :
          <PricingInfo
            subscriptionToShow={prioritySubscription}
            currentSubscription={currentlySelectedSubscription}
            meteredBillingString={meteredBillingString}
          />
      }
      <div className={styles.buttonContainer}>
        {
          availableSubscriptionsArr.length > 1 &&
          <TertiaryButton onClick={handleSeeMoreOrLess}>
            {
              expandOptions ? t('buttons.see_less') : t('buttons.see_more')
            }
          </TertiaryButton>
        }
        {
          actionButton && getActionButtonComponent(actionButton)
        }
      </div>
    </Paper>
  );

};
